/**
 * @module AdapterConfigForm
 * @package @geenius/adapters/solidjs-ark
 * @description Ark-native SolidJS adapter configuration form using Ark Field
 * and RadioGroup primitives.
 */

import { Field } from "@ark-ui/solid/field";
import { RadioGroup } from "@ark-ui/solid/radio-group";
import type { AdapterDomain, DomainAdapterConfig } from "@geenius/adapters";
import { DOMAIN_LABELS, getProvidersForDomain } from "@geenius/adapters";
import type { JSX } from "solid-js";
import { createEffect, createSignal, For, Show } from "solid-js";
import { classNames } from "../lib/classNames";

/**
 * Props accepted by the Ark Solid adapter configuration form.
 *
 * @property domain Adapter domain being configured in the current dialog.
 * @property initialConfig Existing configuration values used to hydrate the form.
 * @property onSave Async or sync handler that persists the submitted configuration.
 * @property onCancel Optional callback invoked when the user dismisses the form.
 * @property class Optional class merged onto the form root.
 * @property titleId Optional identifier applied to the form title for dialog labelling.
 */
export interface AdapterConfigFormProps {
  domain: AdapterDomain;
  initialConfig?: DomainAdapterConfig;
  onSave: (config: DomainAdapterConfig) => Promise<void> | void;
  onCancel?: () => void;
  class?: string;
  titleId?: string;
}

function providerOptionClass(selected: boolean): string {
  return classNames(
    "flex cursor-pointer items-start gap-3 rounded-md border p-4 text-left transition-colors",
    selected
      ? "border-slate-950 bg-slate-950 text-white shadow-sm"
      : "border-slate-200 bg-white text-slate-950 hover:border-slate-300 hover:bg-slate-50",
  );
}

function tierClass(tier: "pronto" | "lancio" | "studio"): string {
  if (tier === "pronto") {
    return "bg-emerald-100 text-emerald-900";
  }
  if (tier === "lancio") {
    return "bg-amber-100 text-amber-950";
  }
  return "bg-slate-100 text-slate-600";
}

/** Renders an Ark UI Solid provider and credential form. */
export function AdapterConfigForm(props: AdapterConfigFormProps): JSX.Element {
  const providers = getProvidersForDomain(props.domain);
  const [provider, setProvider] = createSignal(
    props.initialConfig?.provider ?? providers[0]?.id ?? "",
  );
  const [apiKey, setApiKey] = createSignal(props.initialConfig?.apiKey ?? "");
  const [baseUrl, setBaseUrl] = createSignal(
    props.initialConfig?.baseUrl ?? "",
  );
  const [isSaving, setIsSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const selectedMeta = () => providers.find((entry) => entry.id === provider());

  const needsApiKey = () => selectedMeta()?.requiresApiKey ?? false;
  const needsBaseUrl = () => selectedMeta()?.requiresBaseUrl ?? false;

  createEffect(() => {
    if (!needsApiKey() && apiKey()) {
      setApiKey("");
    }
    if (!needsBaseUrl() && baseUrl()) {
      setBaseUrl("");
    }
  });

  const handleSubmit = async (event: Event): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (!provider()) {
      setError("Select a provider");
      return;
    }

    if (needsApiKey() && !apiKey().trim()) {
      setError("API key is required");
      return;
    }

    setIsSaving(true);
    try {
      const config: DomainAdapterConfig = { provider: provider() };
      if (needsApiKey() && apiKey().trim()) {
        config.apiKey = apiKey().trim();
      }
      if (needsBaseUrl() && baseUrl().trim()) {
        config.baseUrl = baseUrl().trim();
      }
      await props.onSave(config);
    } catch {
      setError("Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      class={classNames(
        "space-y-6 rounded-md border border-slate-200 bg-white p-6 text-slate-950 shadow-sm",
        props.class,
      )}
      data-gn-adapters-ark-component="adapter-config-form"
      noValidate
      onSubmit={handleSubmit}
    >
      <div>
        <h3 class="text-lg font-bold text-slate-950" id={props.titleId}>
          Configure {DOMAIN_LABELS[props.domain]}
        </h3>
        <p class="mt-1 text-sm text-slate-500">
          Select and configure your preferred provider.
        </p>
      </div>

      <Show when={error()}>
        <div
          class="rounded-md border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-950"
          role="alert"
        >
          {error()}
        </div>
      </Show>

      <RadioGroup.Root
        class="space-y-3"
        onValueChange={(details) => {
          if (details.value) {
            setProvider(details.value);
          }
        }}
        value={provider()}
      >
        <RadioGroup.Label class="block text-xs font-semibold uppercase text-slate-500">
          Provider
        </RadioGroup.Label>
        <div class="grid gap-2 sm:grid-cols-2">
          <For each={providers}>
            {(providerMeta) => (
              <RadioGroup.Item
                class={providerOptionClass(provider() === providerMeta.id)}
                onClick={() => setProvider(providerMeta.id)}
                value={providerMeta.id}
              >
                <RadioGroup.ItemHiddenInput />
                <RadioGroup.ItemControl class="mt-0.5 flex h-4 w-4 items-center justify-center rounded-full border border-current">
                  <RadioGroup.Indicator class="h-2 w-2 rounded-full bg-current" />
                </RadioGroup.ItemControl>
                <RadioGroup.ItemText class="min-w-0 flex-1">
                  <span class="block text-sm font-bold">
                    {providerMeta.name}
                  </span>
                  <span class="mt-1 block text-xs leading-relaxed opacity-75">
                    {providerMeta.description}
                  </span>
                  <span
                    class={classNames(
                      "mt-2 inline-flex rounded px-2 py-0.5 text-[0.5rem] font-bold uppercase",
                      tierClass(providerMeta.tier),
                    )}
                  >
                    {providerMeta.tier}
                  </span>
                </RadioGroup.ItemText>
              </RadioGroup.Item>
            )}
          </For>
        </div>
      </RadioGroup.Root>

      <Show when={needsApiKey()}>
        <Field.Root class="space-y-1.5" required>
          <Field.Label class="block text-xs font-semibold uppercase text-slate-500">
            API Key
          </Field.Label>
          <Field.Input
            autocomplete="off"
            class="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-950 shadow-sm outline-none transition-colors placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-400/20"
            onInput={(event) => setApiKey(event.currentTarget.value)}
            placeholder={`Enter ${selectedMeta()?.name ?? provider() ?? "provider"} API key`}
            type="password"
            value={apiKey()}
          />
        </Field.Root>
      </Show>

      <Show when={needsBaseUrl()}>
        <Field.Root class="space-y-1.5">
          <Field.Label class="block text-xs font-semibold uppercase text-slate-500">
            Base URL
          </Field.Label>
          <Field.Input
            class="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-950 shadow-sm outline-none transition-colors placeholder:text-slate-400 focus:border-slate-400 focus:ring-2 focus:ring-slate-400/20"
            onInput={(event) => setBaseUrl(event.currentTarget.value)}
            placeholder="https://..."
            type="url"
            value={baseUrl()}
          />
        </Field.Root>
      </Show>

      <div class="flex justify-end gap-3 pt-2">
        <Show when={props.onCancel !== undefined}>
          <button
            class="rounded-md px-5 py-2.5 text-sm font-semibold text-slate-500 transition-colors hover:text-slate-950"
            onClick={() => props.onCancel?.()}
            type="button"
          >
            Cancel
          </button>
        </Show>
        <button
          class="inline-flex items-center justify-center rounded-md border border-slate-950 bg-slate-950 px-8 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-slate-800 disabled:pointer-events-none disabled:opacity-50"
          disabled={isSaving() || !provider()}
          type="submit"
        >
          {isSaving() ? "Saving..." : "Save Configuration"}
        </button>
      </div>
    </form>
  );
}
